import type { ExpenseSettlementListRow } from '../domain/expenseTypes';
import { matchesSearchAny } from '../../shared/format/searchText';

type BranchSettlementFilters = {
  branchId: string;
  dateFrom: string;
  dateTo: string;
  method?: string | null;
  settlementStatus?: string | null;
  search?: string | null;
};

type ExpensesSnapshot = {
  settlements: ExpenseSettlementListRow[];
  expenses: Record<string, unknown>[];
};

/**
 * Filtra settlements del snapshot cache.
 * Scoping estricto por sucursal: el pago pertenece a la factura padre, así que
 * se usa `expenseBranchId` (ya viene derivado de la RPC) y, como respaldo, el
 * branch de la factura en el cache. Si no hay metadata de sucursal → se excluye
 * (un dato sin dueño conocido nunca es un allow implícito).
 */
function filterCachedBranchSettlements(
  cached: ExpensesSnapshot,
  query: BranchSettlementFilters
): ExpenseSettlementListRow[] {
  const from = query.dateFrom;
  const to = query.dateTo;
  const search = query.search?.trim() ?? '';
  const expenseBranch = new Map(
    cached.expenses.map((e) => [String(e.id), String(e.branch_id ?? '')] as const)
  );
  return cached.settlements.filter((s) => {
    if (s.paidAt < from || s.paidAt > to) return false;
    if (query.method && query.method !== 'all' && s.method !== query.method) return false;
    if (query.settlementStatus === 'active' && s.settlementStatus !== 'active') return false;
    if (query.settlementStatus === 'voided' && s.settlementStatus !== 'voided') return false;
    if (
      search &&
      !matchesSearchAny([s.supplierName, s.expenseDescription, s.note, s.categoryName], search)
    ) {
      return false;
    }
    const bid = s.expenseBranchId ?? expenseBranch.get(s.expenseId) ?? null;
    return bid === query.branchId;
  });
}

async function readExpensesSnapshot(): Promise<ExpensesSnapshot | null> {
  const { getWebExpensesSnapshotCache } = await import('./webExpensesSnapshotCache');
  return getWebExpensesSnapshotCache();
}

export async function listBranchSettlementsLocal(query: BranchSettlementFilters & {
  limit: number;
  offset: number;
}): Promise<ExpenseSettlementListRow[]> {
    const cached = await readExpensesSnapshot();
        if (!cached) return [];
        const rows = filterCachedBranchSettlements(cached, query);
        rows.sort((a, b) => (a.paidAt < b.paidAt ? 1 : a.paidAt > b.paidAt ? -1 : 0));
        return rows.slice(query.offset, query.offset + query.limit);
}

export async function countBranchSettlementsLocal(query: BranchSettlementFilters): Promise<number> {
  const cached = await readExpensesSnapshot();
  if (!cached) return 0;
  return filterCachedBranchSettlements(cached, query).length;
}

export async function summarizeBranchSettlementsLocal(query: BranchSettlementFilters): Promise<{
  count: number;
  totalPaid: number;
  cashOutflowTotal: number;
}> {
  const cached = await readExpensesSnapshot();
  if (!cached) return { count: 0, totalPaid: 0, cashOutflowTotal: 0 };
  const rows = filterCachedBranchSettlements(cached, query);
  // "count" describe al conjunto visible (paridad con la lista: con el filtro
  // "Anulados" los KPIs muestran cuántos pagos anulados hay, no 0).
  // Los montos solo suman pagos activos — un pago anulado no salió de la caja.
  const activeRows = rows.filter((s) => s.settlementStatus === 'active');
  return {
    count: rows.length,
    totalPaid: activeRows.reduce((sum, s) => sum + s.settlementAmount, 0),
    cashOutflowTotal: activeRows
      .filter((s) => s.cashOutflowId != null)
      .reduce((sum, s) => sum + s.settlementAmount, 0),
  };
}
